<script setup>
import Button from "@/components/ui/button/Button.vue";
import Card from "@/components/ui/card/Card.vue";
import CardContent from "@/components/ui/card/CardContent.vue";
import CardFooter from "@/components/ui/card/CardFooter.vue";
import CardHeader from "@/components/ui/card/CardHeader.vue";
import { isDarkMode } from "@/darkmode.js";
import {
  CalendarDaysIcon,
  ChevronDown,
  LaptopMinimalIcon,
  MapPinIcon,
  PencilIcon,
} from "lucide-vue-next";
import { ref } from 'vue'; // Import ref for reactive variables

const emit = defineEmits(['dismiss']); // Declare custom events

defineProps({
  name: String,
  img: {
    type: String,
  },
  date: String,
  location: String,
  description: String,
  seat: {
    type: String,
    required: false,
  },
  signedUp: Boolean,
  ongoing: String,
  id: String,
  userAuthenticated: {type: Boolean, default: false},
});

const signUpPath = ref(`/profile`); // Path to sign-in page

// Swipe to dismiss logic
const startY = ref(0);
const currentY = ref(0);
const isDragging = ref(false);
const dragOffset = ref(0); // For visual feedback during drag
const cardFooterRef = ref(null); // Ref for the scrollable CardFooter
const canDismiss = ref(true);

function startDrag(e) {
  isDragging.value = true;
  startY.value = e.touches[0].clientY;
  currentY.value = e.touches[0].clientY;
  dragOffset.value = 0;

  const footer = cardFooterRef.value?.$el;
  if (footer && footer.contains(e.target)) {
    // If touch starts inside the scrollable footer and it's not at the top,
    // then this is a scroll gesture for sure.
    if (footer.scrollHeight > footer.clientHeight && footer.scrollTop > 0) {
      canDismiss.value = false;
      return;
    }
  }
  // Otherwise, we'll assume it's a dismiss gesture until proven otherwise in onDrag.
  canDismiss.value = true;
}

function onDrag(e) {
  if (!isDragging.value) return;

  // If we've already decided this is a scroll-only gesture, do nothing.
  if (!canDismiss.value) return;

  currentY.value = e.touches[0].clientY;
  const diff = currentY.value - startY.value;

  const footer = cardFooterRef.value?.$el;

  // Check if we are dragging up inside the footer.
  if (diff < 0 && footer && footer.contains(e.target)) {
    // User is dragging up inside the footer, so this is a scroll gesture.
    // We should let the browser handle it.
    canDismiss.value = false; // Mark as scroll gesture
    return; // Don't prevent default
  }

  // If we are here, it's a dismiss gesture (or a gesture that does nothing useful).
  e.preventDefault();

  if (diff > 0) { // Only allow dragging down for dismiss
    dragOffset.value = diff;
  } else {
    dragOffset.value = 0;
  }
}

function endDrag() {
  if (!isDragging.value) return;

  if (canDismiss.value) {
    if (dragOffset.value > 100) { // Threshold for dismissal
      emit('dismiss');
    }
  }

  isDragging.value = false;
  dragOffset.value = 0; // Reset drag offset
}

</script>

<template>
  <div class="p-0"
    @touchstart="startDrag"
    @touchmove="onDrag"
    @touchend="endDrag"
    :style="isDragging && canDismiss ? { bottom: -dragOffset + 'px' } : {}"
  >
    <Card class="p-0 w-full z-10 rounded-md gap-0 h-[calc(88dvh+1px)] flex flex-col rounded-b-none border-b-none">
      <CardHeader class="p-0 w-full h-32 top-0 z-0 rounded-t-md" :style="{
        backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%), url('${img}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }"><Button variant="ghost" class="" @click="emit('dismiss')">
          <ChevronDown :color="isDarkMode ? '#ffffff' : '#000000'" />
        </Button>
        <div class="font-bold px-3 text-primary-foreground flex items-end h-20 pb-2 dark:text-primary-foreground">
          {{ name }}
        </div>
      </CardHeader>
      <div class="h-full flex flex-col">
        <CardContent class="px-0 flex-shrink-0">
          <div v-if="!signedUp" class="border-b px-3 flex items-start py-4">
            <div class="grid grid-cols-2 gap-4 w-full text-sm">
              <div class="flex items-center">
                <CalendarDaysIcon class="w-4 h-4 mr-1" /> {{ date }}
              </div>
              <div class="flex items-center">
                <MapPinIcon class="w-4 h-4 mr-1" /> {{ location }}
              </div>
              <div class="col-span-2">
                <RouterLink v-if="(userAuthenticated)" :to="`/signup/${id}`">
                  <Button class="w-full">Sign up</Button>
                </RouterLink>
                <RouterLink v-else :to="signUpPath">
                  <Button class="w-full">Sign in</Button>
                </RouterLink>
              </div>
            </div>
          </div>
          <div v-if="signedUp" class="border-b px-3 flex items-start py-2">
            <div class="grid grid-cols-2 gap-4 w-full text-sm">
              <div class="flex items-center">
                <CalendarDaysIcon class="w-4 h-4 mr-1" /> {{ date }}
              </div>
              <div class="flex items-center">
                <MapPinIcon class="w-4 h-4 mr-1" /> {{ location }}
              </div>
            </div>
          </div>
          <div v-if="signedUp" class="border-b px-3 flex items-center py-2">
            <div class="grid grid-cols-2 gap-4 w-full text-sm">
              <div class="flex items-center">
                <LaptopMinimalIcon class="w-4 h-4 mr-1" /> Seat: {{ seat }}
              </div>
                <RouterLink :to="`/signup/${id}`" class="flex items-center">
                  <Button size="sm" class="w-full gap-4">
                    Change
                    <PencilIcon />
                  </Button>
                </RouterLink>
            </div>
          </div>

          <div v-if="signedUp" class="border-b px-3 flex items-start py-4">
            <div class="grid grid-cols-2 gap-4 w-full text-sm">
              <div class="col-span-2">
                <RouterLink :to="`/steam-game/${id}`">
                  <Button class="w-full">Find Interested Players!</Button>
                </RouterLink>
              </div>
            </div>
          </div>
          
        </CardContent>
        <CardFooter ref="cardFooterRef" class="px-3 pt-1 flex-grow overflow-y-auto flex flex-col items-start w-full">
          <div class="flex-1 w-full">
            <h1 class="font-semibold">Event Information</h1>
            <p class="text-sm flex-1 w-full whitespace-pre-wrap">{{ description }}</p>
          </div>
        </CardFooter>
      </div>
    </Card>
  </div>
</template>
